import type { JSX } from "react";
// Settings catalog page.
//
// One row per registered setting (from SETTINGS_REGISTRY, merged with DB
// overrides). FE-only search + tag filter. Generic editor per type.
// Encrypted_* never echo plaintext — UI shows a password input and a
// "secret set" / "not set" badge.
//
// URL shape (cleveris-ux-ui pattern — every visible detail in the URL):
//   ?tag=integration:ibkr       filter to one tag
//   ?q=foo                       free-text search

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api, type Account, type SettingPublic } from "../api.js";
import { initialDraft, normalize } from "../settings-draft.js";
import { Field, Select, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Badge } from "../components/ui/badge.js";

export function SettingsCatalog(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag") ?? "";
  const q = params.get("q") ?? "";

  const list = useQuery({
    queryKey: ["settings", tag, q],
    queryFn: () => {
      const params: { tag?: string; q?: string } = {};
      if (tag) params.tag = tag;
      if (q) params.q = q;
      return api.settings(params);
    },
  });

  const allTags = useMemo(() => {
    const set = new Set<string>();
    list.data?.items.forEach((s) => {
      s.tags.forEach((t) => set.add(t));
    });
    return Array.from(set).sort();
  }, [list.data]);

  const updateParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  return (
    <>
      <PageHeader title="Settings catalog" />
      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4 [&_label]:mb-0">
        <div className="min-w-[220px] flex-1">
          <Field label="Search">
            <TextInput
              value={q}
              placeholder="Search by code, title, or description…"
              onChange={(e) => {
                updateParam("q", e.target.value);
              }}
            />
          </Field>
        </div>
        <div className="w-full sm:w-72">
          <Field label="Tag">
            <Select
              value={tag}
              onChange={(e) => {
                updateParam("tag", e.target.value);
              }}
            >
              <option value="">All tags</option>
              {allTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      {list.isLoading && <div className="text-muted-foreground text-sm">Loading…</div>}
      {list.isError && <div className="text-bad text-sm">Failed to load settings.</div>}
      {list.data?.items.length === 0 && (
        <div className="text-muted-foreground text-sm">No settings match.</div>
      )}

      <div className="flex flex-col gap-3">
        {list.data?.items.map((s) => (
          <SettingRow key={s.code} setting={s} />
        ))}
      </div>
    </>
  );
}

function SettingRow({ setting }: { setting: SettingPublic }): JSX.Element {
  const qc = useQueryClient();
  // Draft is unknown-typed; SettingEditor + parseDraft handle the round-trip
  // per setting type.
  const [draft, setDraft] = useState<unknown>(initialDraft(setting));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => api.settingUpdate(setting.code, { value: normalize(setting.type, draft) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["settings"] });
      setError(null);
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });
  const clear = useMutation({
    mutationFn: () => api.settingClear(setting.code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["settings"] });
      setDraft(initialDraft({ ...setting, value: null, has_value: false }));
      setError(null);
    },
  });

  return (
    <Card className="gap-3 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{setting.title}</div>
          <code className="text-muted-foreground text-[11px]">{setting.code}</code>
          {setting.description && (
            <div className="text-muted-foreground mt-1.5 text-sm">{setting.description}</div>
          )}
          <div className="text-muted-foreground mt-1.5 text-[11px]">
            <strong>type:</strong> {setting.type}
            {setting.tags.length > 0 && (
              <>
                {" "}
                · <strong>tags:</strong> {setting.tags.join(", ")}
              </>
            )}
            {setting.has_override ? " · override set" : " · catalog default"}
          </div>
        </div>
        <SettingStatusBadge setting={setting} />
      </div>

      <div>
        <SettingEditor setting={setting} value={draft} onChange={setDraft} />
      </div>
      <div className="flex gap-2">
        <Button
          onClick={() => {
            save.mutate();
          }}
          disabled={save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        {setting.has_override && (
          <Button
            variant="outline"
            onClick={() => {
              if (confirm(`Clear override on ${setting.code}?`)) clear.mutate();
            }}
            disabled={clear.isPending}
          >
            Clear
          </Button>
        )}
      </div>
      {error && <div className="text-bad text-sm">{error}</div>}
    </Card>
  );
}

function SettingStatusBadge({ setting }: { setting: SettingPublic }): JSX.Element {
  const label = setting.has_value
    ? setting.type === "encrypted_string"
      ? "secret set"
      : setting.type === "account_ids" && Array.isArray(setting.value)
        ? `${String(setting.value.length)} linked`
        : "set"
    : setting.type === "account_ids"
      ? "none linked"
      : "not set";
  return (
    <Badge variant={setting.has_value ? "good" : "muted"} className="uppercase">
      {label}
    </Badge>
  );
}

// ---------- Editors ----------

function SettingEditor({
  setting,
  value,
  onChange,
}: {
  setting: SettingPublic;
  value: unknown;
  onChange: (v: unknown) => void;
}): JSX.Element {
  if (setting.type === "boolean") {
    return (
      <Select
        value={String(Boolean(value))}
        onChange={(e) => {
          onChange(e.target.value === "true");
        }}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </Select>
    );
  }
  if (setting.type === "number") {
    return (
      <TextInput
        type="number"
        value={typeof value === "string" || typeof value === "number" ? String(value) : ""}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder="0"
      />
    );
  }
  if (setting.type === "encrypted_string") {
    return (
      <TextInput
        type="password"
        autoComplete="new-password"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder="••••••••  (write-only — current value never shown)"
      />
    );
  }
  if (setting.type === "account_ids") {
    return (
      <AccountIdsEditor
        setting={setting}
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={onChange}
      />
    );
  }
  return (
    <TextInput
      value={typeof value === "string" ? value : ""}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      placeholder=""
    />
  );
}

function AccountIdsEditor({
  setting,
  value,
  onChange,
}: {
  setting: SettingPublic;
  value: string[];
  onChange: (v: string[]) => void;
}): JSX.Element {
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.list<Account>("/accounts") });
  const allAccounts = accountsQuery.data?.items ?? [];
  const acceptKinds = setting.accept_account_kinds;
  const candidates = acceptKinds ? allAccounts.filter((a) => acceptKinds.includes(a.kind)) : allAccounts;
  const linked = candidates.filter((a) => value.includes(a.id));
  const unlinked = candidates.filter((a) => !value.includes(a.id));

  const addId = (id: string) => {
    if (!id) return;
    onChange([...value, id]);
  };
  const removeId = (id: string) => {
    onChange(value.filter((v) => v !== id));
  };

  return (
    <div className="flex flex-col gap-2">
      {linked.length === 0 && (
        <div className="text-muted-foreground text-sm">
          No accounts linked yet.
          {acceptKinds && acceptKinds.length > 0 && (
            <>
              {" "}
              Create one under <Link to="/accounts">Accounts</Link> (kind ={" "}
              <code>{acceptKinds.join(", ")}</code>) first.
            </>
          )}
        </div>
      )}
      {linked.map((a) => (
        <div
          key={a.id}
          className="bg-muted flex items-center justify-between gap-2 rounded-md border p-3"
        >
          <div className="min-w-0">
            <div className="font-medium">{a.name}</div>
            <div className="text-muted-foreground text-[11px]">
              {a.kind} · {a.institution ?? "—"} · {a.currency}
              {a.last_synced_at && <> · last synced {new Date(a.last_synced_at).toLocaleString()}</>}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              removeId(a.id);
            }}
          >
            Remove
          </Button>
        </div>
      ))}
      {unlinked.length > 0 ? (
        <Select
          value=""
          onChange={(e) => {
            addId(e.target.value);
          }}
        >
          <option value="">+ Add account…</option>
          {unlinked.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.kind} · {a.currency})
            </option>
          ))}
        </Select>
      ) : (
        linked.length > 0 && (
          // Every eligible account is already linked — explain why there's no
          // "+ Add account" control (e.g. you only have one broker account).
          <div className="text-muted-foreground text-[11px]">
            To link another, create a new
            {acceptKinds && acceptKinds.length > 0 ? (
              <>
                {" "}
                <code>{acceptKinds.join(", ")}</code>
              </>
            ) : null}{" "}
            account under <Link to="/accounts">Accounts</Link>. Removing one here frees it to be
            re-added.
          </div>
        )
      )}
    </div>
  );
}
